import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SKILLS = [
  "Animal care", "Gardening", "Property watching", "Bee-keeping", "Wood-cutting",
  "Mending fences", "Herb knowledge", "Quiet companionship", "Cooking", "Writing letters",
];

export default function ProfileEdit() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    role: "both",
    bio: "",
    region: "USA",
    location: "",
    skills: [],
    silence_preference: "occasional-words",
    duration_sought: "",
    avatar_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        role: user.role || "both",
        bio: user.bio || "",
        region: user.region || "USA",
        location: user.location || "",
        skills: user.skills || [],
        silence_preference: user.silence_preference || "occasional-words",
        duration_sought: user.duration_sought || "",
        avatar_url: user.avatar_url || "",
      });
    }
  }, [user]);

  const toggleSkill = (s) => {
    setForm((f) => ({ ...f, skills: f.skills.includes(s) ? f.skills.filter((x) => x !== s) : [...f.skills, s] }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch("/profile", form);
      setUser(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12">
      <p className="label-overline">Your profile</p>
      <h1 className="font-display text-5xl mt-2 mb-2">Compose thy letter of self</h1>
      <div className="fleuron mb-8">❦</div>

      <form onSubmit={onSubmit} className="parchment-card space-y-6" data-testid="profile-form">
        <div>
          <label className="label-overline block mb-2">I am a…</label>
          <div className="grid grid-cols-3 gap-2">
            {[{ v: "hermit", l: "Hermit" }, { v: "host", l: "Host" }, { v: "both", l: "Either" }].map((opt) => (
              <button
                type="button"
                key={opt.v}
                data-testid={`profile-role-${opt.v}`}
                onClick={() => setForm({ ...form, role: opt.v })}
                className={`py-3 border text-sm font-body transition-colors ${
                  form.role === opt.v ? "bg-ink-black text-parchment-faded border-ink-black" : "border-rule hover:bg-parchment-bone"
                }`}
              >{opt.l}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="label-overline block mb-2">Bio · your letter of self</label>
          <textarea
            data-testid="profile-bio"
            className="field"
            rows={6}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder="I am forty-two years old. I prune apple trees and keep a beehive alive…"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-overline block mb-2">Region</label>
            <select data-testid="profile-region" className="field" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
              <option>USA</option><option>UK</option><option>Other</option>
            </select>
          </div>
          <div>
            <label className="label-overline block mb-2">Where, more precisely</label>
            <input data-testid="profile-location" className="field" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Yorkshire · Vermont · Cornwall…" />
          </div>
        </div>

        <div>
          <label className="label-overline block mb-2">Skills</label>
          <div className="flex flex-wrap gap-2" data-testid="profile-skills">
            {SKILLS.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => toggleSkill(s)}
                className={`px-3 py-1.5 border text-sm font-body transition-colors ${
                  form.skills.includes(s) ? "bg-moss text-parchment-faded border-moss" : "border-rule hover:bg-parchment-bone"
                }`}
              >{s}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-overline block mb-2">Silence preference</label>
            <select data-testid="profile-silence" className="field" value={form.silence_preference} onChange={(e) => setForm({ ...form, silence_preference: e.target.value })}>
              <option value="seen-but-not-heard">Seen, but not heard</option>
              <option value="occasional-words">A few quiet words</option>
              <option value="open-to-conversation">Open to conversation</option>
            </select>
          </div>
          <div>
            <label className="label-overline block mb-2">Duration sought</label>
            <input data-testid="profile-duration" className="field" value={form.duration_sought} onChange={(e) => setForm({ ...form, duration_sought: e.target.value })} placeholder="One year · A season · Open" />
          </div>
        </div>

        <div>
          <label className="label-overline block mb-2">Portrait URL (optional)</label>
          <input data-testid="profile-avatar" className="field" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" />
        </div>

        {error && <p className="text-wax-red text-sm italic">{error}</p>}
        {saved && <p className="text-moss text-sm italic">Saved. The wax holds.</p>}
        <div className="flex gap-3">
          <button type="button" onClick={() => navigate("/dashboard")} className="ghost-button flex-1">Back</button>
          <button data-testid="profile-save" disabled={saving} className="wax-button flex-1">{saving ? "Sealing…" : "Save profile"}</button>
        </div>
      </form>
    </main>
  );
}
